import { slotLabel } from '../lib/slotLabel';

/**
 * The wardrobe's five categories (spec 2026-09-14-wardrobe-category-dock, D1). The 22 cosmetic
 * slots are content (image_assets.md §6) and stay per-slot in the compositor; this is the
 * PRESENTATION grouping the dock and drawer use. Client-side by decision (D2) — a `group` field on
 * each slot would push a UI choice through schema + DTO for no runtime benefit. What keeps this
 * honest is `slotGroups.contentDrift.test.ts`: every pack slot in exactly one group, or red.
 */
export type SlotGroupCode = 'head' | 'body' | 'paws' | 'back' | 'scene';

export type SlotGroup = {
  code: SlotGroupCode;
  /** The tab's visible label, the drawer title, and the accessible name. One string for all
   *  three — a one-word `shortLabel` existed while the categories were plates on a 48-wide dock,
   *  and "Back" alone read as *go back*; the strip (2026-09-14, evening) has room for the words. */
  label: string;
  slots: readonly string[];
};

export const SLOT_GROUPS: readonly SlotGroup[] = [
  { code: 'head', label: 'Head', slots: ['headwear', 'hair_mane', 'ears_horns', 'face', 'eyewear'] },
  { code: 'body', label: 'Body', slots: ['body_under', 'body_main', 'body_over', 'belt', 'neck_collar', 'shoulder'] },
  { code: 'paws', label: 'Paws & Held', slots: ['hands_gloves', 'held_left', 'held_right', 'legs_lower', 'feet'] },
  { code: 'back', label: 'Back & Tail', slots: ['wings_back', 'tail_wrap'] },
  { code: 'scene', label: 'Scene & Glow', slots: ['backdrop', 'foreground', 'aura_back', 'aura_front'] },
];

const GROUP_BY_SLOT: ReadonlyMap<string, SlotGroup> = new Map(
  SLOT_GROUPS.flatMap((g) => g.slots.map((s) => [s, g] as const)),
);

export function groupOf(slotCode: string): SlotGroup | null {
  return GROUP_BY_SLOT.get(slotCode) ?? null;
}

/**
 * One-word "where does this go" caption under a cell when its group spans several slots. Distinct
 * from `lib/slotLabel`, which is the long form the rest of the app (bag, item detail) uses.
 */
export const SLOT_CAPTIONS: Readonly<Record<string, string>> = {
  headwear: 'Hat', hair_mane: 'Mane', ears_horns: 'Ears', face: 'Face', eyewear: 'Glasses',
  body_under: 'Under', body_main: 'Top', body_over: 'Over', belt: 'Belt', neck_collar: 'Collar', shoulder: 'Shoulder',
  hands_gloves: 'Gloves', held_left: 'Left hand', held_right: 'Right hand', legs_lower: 'Legs', feet: 'Feet',
  wings_back: 'Wings', tail_wrap: 'Tail',
  backdrop: 'Backdrop', foreground: 'Front', aura_back: 'Glow back', aura_front: 'Glow front',
};

export function slotCaption(slotCode: string): string {
  return SLOT_CAPTIONS[slotCode] ?? slotLabel(slotCode);
}
